// src/pages/Trend/components/analysis/ingredient-analysis/RankingChart.js
import React, { memo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { ErrorMessage } from '../../../../../components/common/UI';
import './RankingChart.css';

// BBC Style Configuration - matching other charts
const BBC_STYLE = {
  FONT_FAMILY: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  PRIMARY_COLOR: '#1380A1',
  SECONDARY_COLOR: '#FAAB18',
  HIGHLIGHT_COLOR: '#D55E00',
  TEXT_COLOR: '#333333',
  LIGHT_TEXT: '#666666',
  AXIS_COLOR: '#999999',
};

// Render filter label with monospace number for alignment
const renderFilterLabel = (label) => {
  const match = label.match(/^(\d+)(.*)$/);
  if (match) {
    return (
      <>
        <span className="filter-label-number">{match[1]}</span>
        {match[2]}
      </>
    );
  }
  return label;
};

// Process chart data
const processChartData = (data) => {
  if (!data?.most_common_ingredients) return [];

  const { labels, values } = data.most_common_ingredients;
  return labels.map((label, index) => ({
    name: label,
    value: values[index]
  }));
};

// Narrative Component
const RankingNarrative = ({ primary, secondary, hasSecondaryData, translate }) => {
  if (!primary?.most_common_ingredients) return null;

  const topIngredient = primary.most_common_ingredients.labels[0];
  const topCount = primary.most_common_ingredients.values[0];
  const totalIngredients = primary.most_common_ingredients.labels.length;
  const topThreeSum = primary.most_common_ingredients.values.slice(0, 3).reduce((a, b) => a + b, 0);
  const totalProducts = primary.most_common_ingredients.values.reduce((a, b) => a + b, 0);
  const topThreePercent = ((topThreeSum / totalProducts) * 100).toFixed(1);

  if (!hasSecondaryData) {
    return (
      <div className="ranking-narrative">
        <h2 className="narrative-title">
          <span className="highlight-ingredient" style={{ color: BBC_STYLE.HIGHLIGHT_COLOR }}>
            {topIngredient}
          </span>
          {' '}{translate('trendPage.ingredientAnalysis.narratives.ingredientUsed')}{' '}
          <span className="highlight-count">
            {topCount}{translate('trendPage.ingredientAnalysis.units.products')}
          </span>
          {' '}{translate('trendPage.ingredientAnalysis.narratives.mostUsed')}
        </h2>
        <div className="narrative-subtitle">
          <span>{translate('trendPage.ingredientAnalysis.narratives.top')}</span>
          <span className="stat-number" style={{ color: BBC_STYLE.PRIMARY_COLOR }}>
            {totalIngredients}{translate('trendPage.ingredientAnalysis.units.ingredients')}
          </span>
          <span>{translate('trendPage.ingredientAnalysis.narratives.amongTop3')}</span>
          <span className="stat-pill">
            {topThreeSum}{translate('trendPage.ingredientAnalysis.units.products')}
          </span>
          <span>{translate('trendPage.ingredientAnalysis.narratives.includedIn')} ({topThreePercent}%)</span>
        </div>
      </div>
    );
  }

  // Comparison mode
  const topIngredient2 = secondary.most_common_ingredients.labels[0];
  const topCount2 = secondary.most_common_ingredients.values[0];
  const sameIngredient = topIngredient === topIngredient2;

  return (
    <div className="ranking-narrative">
      <h2 className="narrative-title">
        {sameIngredient
          ? translate('trendPage.ingredientAnalysis.narratives.sameIngredientBoth', { ingredient: topIngredient })
          : translate('trendPage.ingredientAnalysis.narratives.differentIngredients')
        }
      </h2>
      <div className="narrative-comparison">
        <div className="comparison-item">
          <div className="comparison-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
          <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set1'))}:</span>
          <span className="highlight-ingredient" style={{ color: BBC_STYLE.PRIMARY_COLOR }}>
            {topIngredient}
          </span>
          <span className="comparison-count">({topCount})</span>
        </div>
        <div className="comparison-item">
          <div className="comparison-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
          <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set2'))}:</span>
          <span className="highlight-ingredient" style={{ color: BBC_STYLE.SECONDARY_COLOR }}>
            {topIngredient2}
          </span>
          <span className="comparison-count">({topCount2})</span>
        </div>
      </div>
    </div>
  );
};

// Main Component
const RankingChart = ({ data }) => {
  const { translate } = useLanguage();

  // Custom Tooltip - defined inside component to access translate
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="ranking-tooltip">
          <div className="tooltip-ingredient">{payload[0].payload.name}</div>
          <div className="tooltip-value">
            {translate('trendPage.ingredientAnalysis.ranking.usedIn')}: <strong>{payload[0].value.toLocaleString()}</strong>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!data?.primary?.most_common_ingredients) {
    return <ErrorMessage message={translate('trendPage.ingredientAnalysis.errors.noRankingData')} />;
  }

  const { primary, secondary } = data;
  const hasSecondaryData = secondary?.most_common_ingredients !== undefined;

  const primaryData = processChartData(primary);
  const secondaryData = hasSecondaryData ? processChartData(secondary) : null;

  const getMaxDomain = (data) => {
    if (!data || data.length === 0) return 10;
    const maxValue = Math.max(...data.map(item => item.value));
    return Math.ceil(maxValue * 1.1);
  };

  const maxDomain = hasSecondaryData
    ? Math.max(getMaxDomain(primaryData), getMaxDomain(secondaryData))
    : getMaxDomain(primaryData);

  return (
    <div className="ranking-chart-container" style={{ fontFamily: BBC_STYLE.FONT_FAMILY }}>
      {/* Narrative Section */}
      <RankingNarrative
        primary={primary}
        secondary={secondary}
        hasSecondaryData={hasSecondaryData}
        translate={translate}
      />

      {/* Chart Section */}
      <div className="ranking-charts-wrapper">
        {hasSecondaryData ? (
          <>
            <div className="ranking-chart-section">
              <div className="chart-label">
                <span className="label-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
                {translate('trendPage.filters.set1')}
              </div>
              <div className="chart-area">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={primaryData}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
                  >
                    <XAxis
                      type="number"
                      domain={[0, maxDomain]}
                      tick={{ fill: BBC_STYLE.AXIS_COLOR, fontSize: 11 }}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickLine={false}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={90}
                      tick={{ fontSize: 11, fill: BBC_STYLE.TEXT_COLOR }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                      {primaryData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={BBC_STYLE.PRIMARY_COLOR}
                          fillOpacity={index === 0 ? 1 : 0.7 + (0.3 * (1 - index / primaryData.length))}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="ranking-chart-section">
              <div className="chart-label">
                <span className="label-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
                {translate('trendPage.filters.set2')}
              </div>
              <div className="chart-area">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={secondaryData}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
                  >
                    <XAxis
                      type="number"
                      domain={[0, maxDomain]}
                      tick={{ fill: BBC_STYLE.AXIS_COLOR, fontSize: 11 }}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickLine={false}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={90}
                      tick={{ fontSize: 11, fill: BBC_STYLE.TEXT_COLOR }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                      {secondaryData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={BBC_STYLE.SECONDARY_COLOR}
                          fillOpacity={index === 0 ? 1 : 0.7 + (0.3 * (1 - index / secondaryData.length))}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        ) : (
          <div className="ranking-chart-section single">
            <div className="chart-area">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={primaryData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, maxDomain]}
                    tick={{ fill: BBC_STYLE.AXIS_COLOR, fontSize: 11 }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    tickLine={false}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    tick={{ fontSize: 12, fill: BBC_STYLE.TEXT_COLOR }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                    {primaryData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={BBC_STYLE.PRIMARY_COLOR}
                        fillOpacity={index === 0 ? 1 : 0.7 + (0.3 * (1 - index / primaryData.length))}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(RankingChart);
